import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isTransparent = !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-7xl mx-auto">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors duration-200 ${
              isTransparent
                ? "text-background-50 hover:text-background-50/80"
                : "text-foreground-900 hover:text-primary-500"
            }`}
          >
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors duration-200 ${
              isTransparent
                ? "bg-background-50/20 text-background-50"
                : "bg-primary-500 text-background-50"
            }`}>
              <i className="ri-file-list-3-line text-lg"></i>
            </div>
            <span className="font-heading text-lg font-semibold whitespace-nowrap">
              PandabuySheet Hub
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm transition-colors duration-200 whitespace-nowrap ${
                isTransparent
                  ? "text-background-50/90 hover:text-background-50"
                  : "text-foreground-600 hover:text-primary-500"
              }`}
            >
              Home
            </Link>
            <a
              href="#categories"
              className={`text-sm transition-colors duration-200 whitespace-nowrap ${
                isTransparent
                  ? "text-background-50/90 hover:text-background-50"
                  : "text-foreground-600 hover:text-primary-500"
              }`}
            >
              {t("nav.categories")}
            </a>
            <Link
              to="/blog/pandabuy-spreadsheet-guide"
              className={`text-sm transition-colors duration-200 whitespace-nowrap ${
                isTransparent
                  ? "text-background-50/90 hover:text-background-50"
                  : "text-foreground-600 hover:text-primary-500"
              }`}
            >
              Shopping Guide
            </Link>
            <a
              href="#blog"
              className={`text-sm transition-colors duration-200 whitespace-nowrap ${
                isTransparent
                  ? "text-background-50/90 hover:text-background-50"
                  : "text-foreground-600 hover:text-primary-500"
              }`}
            >
              {t("nav.blog")}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                isTransparent
                  ? "bg-background-50/20 text-background-50 border border-background-50/30 hover:bg-background-50/30"
                  : "bg-primary-500 text-background-50 hover:bg-primary-600"
              }`}
            >
              {t("cta.buynow")}
              <i className="ri-arrow-right-up-line text-xs"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              isTransparent
                ? "text-background-50 hover:bg-background-50/15"
                : "text-foreground-700 hover:bg-background-100"
            }`}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col py-3 px-4 gap-1 max-w-7xl mx-auto">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 rounded-md text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors font-medium"
              >
                <i className="ri-home-4-line mr-2"></i>
                Home
              </Link>
              <a
                href="#categories"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 rounded-md text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors"
              >
                <i className="ri-grid-line mr-2"></i>
                {t("nav.categories")}
              </a>
              <Link
                to="/blog/pandabuy-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2.5 rounded-md text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors"
              >
                <i className="ri-book-open-line mr-2"></i>
                Shopping Guide
              </Link>

              <div className="my-2 border-t border-background-200/50"></div>
              <p className="px-3 text-xs text-foreground-400 font-medium uppercase tracking-wider">
                Quick Categories
              </p>
              {categories.slice(0, 8).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-100 transition-colors"
                >
                  <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                  {cat.name}
                </a>
              ))}
              <Link
                to="/#categories"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 rounded-md text-sm text-primary-500 hover:text-primary-600 hover:bg-background-100 transition-colors"
              >
                View All 14 Categories →
              </Link>

              <div className="mt-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-1.5 w-full px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  {t("cta.buynow")}
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}